// Author: Claude (S3 step 2) | 2026-10-25 | Opening files stored in PRIVATE S3 folders.
//
// KYC, signed contracts, payment proofs, invoices, POs, quotations, RFQ documents, dispatch / POD / receipts, support
// tickets, chats, marketplace order documents and supplier certificates are no longer publicly readable. Their stored
// URLs (or keys) are exchanged for a presigned GET, valid for at most 5 minutes, at POST
// /s3Operations/getSignedReadUrl; the API checks that the signed-in user (or the PO / RFQ / service / KYC link session)
// may see the record the file belongs to and answers 404 otherwise.
//
// Callers use open() / signedUrl() / resolve(). In addition installGlobalHandlers() (called once by AppComponent)
// covers the many templates that bind a stored URL straight into the page, so a missed screen can never show an
// S3 "AccessDenied" page or trigger the 403 logout:
//   - a click on <a href="<private S3 URL>"> (also programmatic a.click()) opens the signed URL instead;
//   - window.open(<private S3 URL>, ...) opens the signed URL instead (the window is opened synchronously, so popup
//     blockers do not interfere);
//   - an <img>/<iframe>/<embed>/<source>/<video>/<audio> whose src is a private S3 URL gets the signed URL;
//   - fetch(<private S3 URL>) (the in-page PDF previews: ng2-pdf-viewer / PDF.js load with fetch, and the CSV /
//     support-ticket / product download helpers) fetches the signed URL instead.
// Public folders (logos, product / service images, catalogues, manuals, public T&C templates) are left untouched.
// No HttpClient GET ever goes to S3 for a private file; the token interceptor also never logs a user out for an S3
// response.
//
// Keep PUBLIC_PREFIXES / PUBLIC_ROOT_OBJECTS identical to Supplier-API utils/s3-file-policy.js.
import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WebApiService } from './web-api.service';
import { KycTokenAuthService } from './kyc-token-auth.service';

export const SIGNED_READ_URL_API = '/s3Operations/getSignedReadUrl';

export const PUBLIC_PREFIXES: readonly string[] = [
  'assets/', 'companyLogo/', 'impa-item-images/', 'marketplace-review/', 'product/', 'profile-photos/', 'service/',
  'serviceApprovalClass/', 'summaryVideo/', 'suppCatalogue/', 'sustainablecommitments/', 'terms-conditions/',
  'termsandconditions/', 'userInstructions/', 'vessel/',
];

/** Public keys inside a private folder: the KYC wizard's company logo (kyc/<id>/logo/<file>). */
export const PUBLIC_KEY_PATTERNS: readonly RegExp[] = [/^kyc\/\d+\/logo\/[^/]+$/];

export const PUBLIC_ROOT_OBJECTS: readonly string[] = [
  'Freight Forwarder Details - template.xlsx', 'VSMPS_Admin_Manual_V1.0.0.1.pdf', 'VSMPS_Buyer_Manual _V1.0.0.1.pdf',
  'VSMPS_Buyer_Manual_V1.1.pdf', 'VSMPS_Supplier _Manual_V1.0.0.1.pdf', 'VSMPS_Supplier_Manual_V2.0.0.pdf',
  'countries_states_cities.json', 'csv_xlsx_thumbnail.jpg', 'facebook28px.png', 'facebook28px.svg',
  'imgpsh_fullsize_anim__1_-removebg-preview.png', 'imgpsh_fullsize_anim__1_-removebg-preview1.png',
  'instagram28px.png', 'instagram28px.svg', 'linkedin-logo.png', 'notfound-removebg-preview.png', 'notfound.png',
  'product_carbon_footprint-removebg-preview_1.png',
  'vecteezy_download-and-upload-file-document-from-cloud-animated_51276568.mp4', 'x28px.png', 'x28px.svg',
];

const LINK_TOKEN_KEYS = ['poAccessToken', 'rfqAccessToken', 'servicePoAccessToken', 'serviceRfqAccessToken'];
const VIRTUAL_HOST = /^([a-z0-9][a-z0-9.-]{1,61}[a-z0-9])\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/;
const PATH_STYLE_HOST = /^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/;
const CACHE_MS = 4 * 60 * 1000; // signed URLs live 5 minutes on the server

/** True when `url` points at an S3 endpoint (any bucket). */
export function isS3Url(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return VIRTUAL_HOST.test(host) || PATH_STYLE_HOST.test(host);
  } catch {
    return false;
  }
}

/** The object key of a stored reference (S3 URL or bare key), or null when it is not an S3 file reference. */
export function s3KeyOf(ref: string): string | null {
  if (typeof ref !== 'string') return null;
  const raw = ref.trim();
  if (!raw) return null;
  const decode = (s: string) => { try { return decodeURIComponent(s); } catch { return null; } };
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    // A bare key: only when its first segment looks like a folder (never a relative app route like ./x or /x).
    if (raw.startsWith('/') || raw.startsWith('.')) return null;
    return decode(raw.split('?')[0].split('#')[0]);
  }
  let url: URL;
  try { url = new URL(raw); } catch { return null; }
  const host = url.hostname.toLowerCase();
  const m = host.match(VIRTUAL_HOST);
  if (m && m[1] !== 's3') return decode(url.pathname.slice(1));
  if (PATH_STYLE_HOST.test(host)) {
    const parts = url.pathname.slice(1).split('/');
    parts.shift();
    return decode(parts.join('/'));
  }
  return null;
}

/** The MPS asset buckets (dev, prod) whose private folders are read through signed links (the API accepts its own). */
export const APP_BUCKETS: readonly string[] = ['vsmps-assets-dev', 'mps-asset-prod'];

/** The bucket of an S3 URL (virtual-hosted or path style), or null. */
export function s3BucketOf(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    const m = host.match(VIRTUAL_HOST);
    if (m && m[1] !== 's3') return m[1];
    if (PATH_STYLE_HOST.test(host)) return u.pathname.slice(1).split('/')[0] || null;
    return null;
  } catch {
    return null;
  }
}

export function isPublicKey(key: string): boolean {
  const i = key.indexOf('/');
  if (i <= 0) return PUBLIC_ROOT_OBJECTS.includes(key);
  if (PUBLIC_PREFIXES.includes(key.slice(0, i + 1))) return true;
  return PUBLIC_KEY_PATTERNS.some((re) => re.test(key));
}

/** True for a stored S3 URL whose folder is private (a bare key counts too when `allowBareKey`). */
export function isPrivateFileRef(ref: string, allowBareKey = false): boolean {
  if (typeof ref !== 'string' || !ref) return false;
  // Already a presigned URL (what signedUrl() returns): use as is, never re-sign (that would loop in the media swap).
  if (/[?&](X-Amz-Signature|Signature)=/i.test(ref)) return false;
  const isUrl = /^https?:\/\//i.test(ref.trim());
  if (isUrl && !isS3Url(ref)) return false;
  // Only the MPS asset buckets have private folders; URLs of other buckets (e.g. older IHM vessel images) load as before.
  if (isUrl && !APP_BUCKETS.includes(s3BucketOf(ref) || '')) return false;
  if (!isUrl && !allowBareKey) return false;
  const key = s3KeyOf(ref);
  return !!key && !isPublicKey(key);
}

@Injectable({ providedIn: 'root' })
export class PrivateFileService {
  private cache = new Map<string, { url: string; at: number }>();
  private inflight = new Map<string, Promise<string | null>>();
  private installed = false;

  constructor(
    private webApi: WebApiService,
    private kycToken: KycTokenAuthService,
    private snack: MatSnackBar,
    private zone: NgZone,
  ) {}

  isPrivate(ref: string | null | undefined, allowBareKey = true): boolean {
    return !!ref && isPrivateFileRef(String(ref), allowBareKey);
  }

  /** A presigned GET for a stored file (key or URL), or null when the user may not see it / it does not exist. */
  async signedUrl(ref: string | null | undefined): Promise<string | null> {
    if (!ref) return null;
    const file = String(ref);
    const hit = this.cache.get(file);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.url;
    const running = this.inflight.get(file);
    if (running) return running;
    const p = this.fetchSigned(file).finally(() => this.inflight.delete(file));
    this.inflight.set(file, p);
    return p;
  }

  private async fetchSigned(file: string): Promise<string | null> {
    const body: any = { file };
    const linkTokens = LINK_TOKEN_KEYS.map((k) => { try { return sessionStorage.getItem(k); } catch { return null; } })
      .filter((t): t is string => !!t);
    if (linkTokens.length) body.linkTokens = linkTokens;
    const kyc = this.kycToken.getToken();
    if (kyc) body.kycToken = kyc;
    try {
      const url = await this.webApi.postRequest<any>(SIGNED_READ_URL_API, body);
      if (typeof url === 'string' && /^https:\/\//i.test(url)) {
        this.cache.set(file, { url, at: Date.now() });
        return url;
      }
      return null;
    } catch {
      return null;
    }
  }

  /** The URL to use for display: the ref itself for public files / non-S3 values, a signed URL for private ones. */
  async resolve(ref: string | null | undefined): Promise<string | null> {
    if (!ref) return null;
    return this.isPrivate(ref) ? this.signedUrl(ref) : String(ref);
  }

  /**
   * Opens a stored file. For a private file the window is opened at once (inside the click, so popup blockers allow
   * it) and pointed at the signed URL when it arrives. Returns false when the file could not be opened.
   */
  async open(ref: string | null | undefined, target: string = '_blank'): Promise<boolean> {
    if (!ref) return false;
    if (!this.isPrivate(ref)) {
      nativeOpen(String(ref), target, 'noopener');
      return true;
    }
    const sameTab = target === '_self' || target === '_top' || target === '_parent';
    const win = sameTab ? null : nativeOpen('', target || '_blank', '');
    if (win) { try { win.opener = null; } catch { /* ignore */ } }
    const signed = await this.signedUrl(ref);
    if (!signed) {
      if (win) win.close();
      this.notAvailable();
      return false;
    }
    if (sameTab) window.location.assign(signed);
    else if (win) win.location.href = signed;
    else nativeOpen(signed, target || '_blank', 'noopener');
    return true;
  }

  private notAvailable(): void {
    this.zone.run(() => this.snack.open('This file is not available to you, or it no longer exists.', 'OK', { duration: 3500 }));
  }

  /** Installs the global safety net described at the top of this file. Idempotent. */
  installGlobalHandlers(): void {
    if (this.installed || typeof window === 'undefined' || typeof document === 'undefined') return;
    this.installed = true;
    this.zone.runOutsideAngular(() => {
      const self = this;

      // 1. window.open(<private S3 URL>)
      const originalOpen = window.open;
      nativeOpenRef = originalOpen.bind(window);
      window.open = function (url?: string | URL, target?: string, features?: string): Window | null {
        const href = url == null ? '' : String(url);
        if (href && isPrivateFileRef(href)) {
          const sameTab = target === '_self' || target === '_top' || target === '_parent';
          const win = sameTab ? null : nativeOpenRef!('', target || '_blank', stripNoopener(features));
          if (win) { try { win.opener = null; } catch { /* ignore */ } }
          self.signedUrl(href).then((signed) => {
            if (!signed) { if (win) win.close(); self.notAvailable(); return; }
            if (sameTab) window.location.assign(signed);
            else if (win) win.location.href = signed;
            else nativeOpenRef!(signed, target || '_blank', features);
          });
          return win;
        }
        return nativeOpenRef!(url as any, target, features);
      } as typeof window.open;

      // 1b. fetch(<private S3 URL>): ng2-pdf-viewer (PDF.js PDFFetchStream) and the download helpers. A private file
      // the user may not see resolves to a 404 Response (never a 403, never a request to S3).
      if (typeof window.fetch === 'function') {
        const originalFetch = window.fetch.bind(window);
        window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
          const href = typeof input === 'string' ? input : (input instanceof URL ? input.href : (input && (input as Request).url) || '');
          if (!href || !isPrivateFileRef(href)) return originalFetch(input as any, init);
          return self.signedUrl(href).then((signed) => {
            if (!signed) return new Response('', { status: 404, statusText: 'Not Found' });
            // A Request object keeps its method / headers; only GET-style reads reach a private file.
            return originalFetch(signed, init);
          });
        } as typeof window.fetch;
      }

      // 2. clicks on <a href="<private S3 URL>"> (capture phase, before the browser navigates)
      const onClick = (ev: MouseEvent) => {
        if (ev.defaultPrevented) return;
        if (ev.type === 'auxclick' && ev.button !== 1) return;
        const a = (ev.target as Element | null)?.closest ? (ev.target as Element).closest('a[href]') as HTMLAnchorElement | null : null;
        if (!a || !isPrivateFileRef(a.href)) return;
        ev.preventDefault();
        const target = ev.type === 'auxclick' || ev.ctrlKey || ev.metaKey || a.hasAttribute('download') ? '_blank' : (a.target || '_self');
        self.open(a.href, target);
      };
      document.addEventListener('click', onClick, true);
      document.addEventListener('auxclick', onClick, true);

      // 3. programmatic a.click() on a detached anchor (download helpers)
      const originalClick = HTMLAnchorElement.prototype.click;
      HTMLAnchorElement.prototype.click = function (this: HTMLAnchorElement) {
        if (!this.isConnected && this.href && isPrivateFileRef(this.href)) {
          self.open(this.href, '_blank');
          return;
        }
        return originalClick.call(this);
      };

      // 4. media elements whose src is a private S3 URL
      const MEDIA = 'img,iframe,embed,source,video,audio';
      const swap = (el: Element) => {
        const src = el.getAttribute('src');
        if (!src || !isPrivateFileRef(src)) return;
        el.setAttribute('data-private-src', src);
        el.removeAttribute('src');
        self.signedUrl(src).then((signed) => {
          // Only if the binding has not moved on to another file meanwhile.
          if (signed && el.getAttribute('data-private-src') === src && !el.getAttribute('src')) el.setAttribute('src', signed);
        });
      };
      const scan = (root: ParentNode) => {
        if ((root as Element).matches && (root as Element).matches(MEDIA)) swap(root as Element);
        root.querySelectorAll && root.querySelectorAll(MEDIA).forEach(swap);
      };
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.target instanceof Element) swap(m.target);
          else m.addedNodes.forEach((n) => { if (n instanceof Element) scan(n); });
        }
      });
      observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });
      scan(document);
    });
  }
}

let nativeOpenRef: ((url?: string | URL, target?: string, features?: string) => Window | null) | null = null;
function nativeOpen(url: string, target?: string, features?: string): Window | null {
  return (nativeOpenRef || window.open.bind(window))(url, target, features);
}

function stripNoopener(features?: string): string {
  // A window opened with noopener returns null, so it could not be pointed at the signed URL; opener is cleared instead.
  return String(features || '').split(',').map((f) => f.trim()).filter((f) => f && !/^noopener|^noreferrer/i.test(f)).join(',');
}
